import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import TableStyle from "styles/application/TableStyle";
import PopupMenu from "components/Elements/PopupMenu";
import { ReactComponent as BlueMenuIcon } from "assets/icons/3dot-icon.svg";
import { ReactComponent as WhiteMenuIcon } from "assets/icons/3dot-white-icon.svg";
import { EDIT_ACCESS, FULL_ACCESS } from "constants/AccessTypes/AccessTypes";

const AT = TableStyle();

const SWITCH_LAYOUT = "switchLayout";

const buildMenuData = ({
	t,
	toggle,
	disableLink,
	customCaptions,
	onEdit,
	onDuplicate,
	onCopy,
	onCopyTaskQuestion,
	onSwitchToServiceLayout,
	onRemove,
}) => [
	{ id: "edit", name: t("action.edit"), handler: () => onEdit(!toggle), isDelete: false },
	{ id: "duplicate", name: t("action.duplicate"), handler: onDuplicate, isDelete: false },
	{ id: "copy", name: t("action.copy"), handler: onCopy, isDelete: false },
	{
		id: "copyQuestions",
		name: t("action.copyEntityQuestions", {
			task: customCaptions?.task,
			questions: customCaptions?.questionPlural,
		}),
		handler: onCopyTaskQuestion,
		isDelete: false,
	},
	{
		id: SWITCH_LAYOUT,
		name: t("action.switchToServiceLayout", { service: customCaptions?.service }),
		handler: onSwitchToServiceLayout,
		isDelete: false,
		disabled: disableLink,
	},
	{ id: "remove", name: t("action.remove"), handler: onRemove, isDelete: true },
];

// Publish-state and access both narrow the menu further, independent of
// which items exist - kept separate from buildMenuData so each stays simple.
const PUBLISHED_ITEMS = ["copy", "copyQuestions", SWITCH_LAYOUT];

const filterMenuData = (menuData, { isPublished, access }) =>
	menuData.filter((item) => {
		if (isPublished) return PUBLISHED_ITEMS.includes(item.id);
		if (access === FULL_ACCESS) return true;
		// The backend accepts Models Edit for all Task operations now, except
		// switching a task to Service Layout, which stays Full-access only.
		if (access === EDIT_ACCESS) return item.id !== SWITCH_LAYOUT;
		return false;
	});

const ModelTaskRowActions = ({
	access,
	toggle,
	index,
	selectedData,
	anchorEl,
	isLast,
	taskId,
	isPublished,
	disableLink,
	customCaptions,
	onDotMenuClick,
	onClickAway,
	onEdit,
	onDuplicate,
	onCopy,
	onCopyTaskQuestion,
	onSwitchToServiceLayout,
	onRemove,
}) => {
	const { t } = useTranslation();
	const menuData = useMemo(
		() =>
			filterMenuData(
				buildMenuData({
					t,
					toggle,
					disableLink,
					customCaptions,
					onEdit,
					onDuplicate,
					onCopy,
					onCopyTaskQuestion,
					onSwitchToServiceLayout,
					onRemove,
				}),
				{ isPublished, access }
			),
		[
			access,
			customCaptions,
			disableLink,
			isPublished,
			onCopy,
			onCopyTaskQuestion,
			onDuplicate,
			onEdit,
			onRemove,
			onSwitchToServiceLayout,
			t,
			toggle,
		]
	);

	return (
		<AT.DotMenu onClick={onDotMenuClick} className="taskdotmenu">
			{(access === FULL_ACCESS || access === EDIT_ACCESS) && (
				<AT.TableMenuButton className="taskdotmenu">
					{toggle ? (
						<WhiteMenuIcon className="taskdotmenu" />
					) : (
						<BlueMenuIcon className="taskdotmenu" />
					)}
				</AT.TableMenuButton>
			)}
			<PopupMenu
				index={index}
				selectedData={selectedData}
				anchorEl={anchorEl}
				isLast={isLast}
				id={taskId}
				clickAwayHandler={onClickAway}
				menuData={menuData}
			/>
		</AT.DotMenu>
	);
};

ModelTaskRowActions.propTypes = {
	access: PropTypes.string,
	toggle: PropTypes.bool.isRequired,
	index: PropTypes.number.isRequired,
	selectedData: PropTypes.number,
	anchorEl: PropTypes.object,
	isLast: PropTypes.bool.isRequired,
	taskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	isPublished: PropTypes.bool,
	disableLink: PropTypes.bool,
	customCaptions: PropTypes.object,
	onDotMenuClick: PropTypes.func.isRequired,
	onClickAway: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDuplicate: PropTypes.func.isRequired,
	onCopy: PropTypes.func.isRequired,
	onCopyTaskQuestion: PropTypes.func.isRequired,
	onSwitchToServiceLayout: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
};

export default ModelTaskRowActions;
